import type { ReactNode } from "react"

import { cn } from "@/lib/utils"

type MarketingBadgeVariant = "outline" | "solid" | "neutral" | "ordinal"
type MarketingBadgeSize = "sm" | "md"

interface MarketingBadgeProps {
  children: ReactNode
  variant?: MarketingBadgeVariant
  size?: MarketingBadgeSize
  className?: string
  "aria-label"?: string
  title?: string
  "aria-hidden"?: boolean
}

const SIZE = {
  sm: "min-h-6 px-2 py-1 text-[0.72rem] tracking-[0.12em]",
  md: "min-h-7 px-3 py-1 text-[0.78rem] tracking-[0.12em]",
} as const

const ORDINAL_SIZE = {
  sm: "text-[0.76rem] tracking-[0.14em]",
  md: "text-[0.82rem] tracking-[0.14em]",
} as const

const VARIANT = {
  outline:
    "rounded-[6px] border border-[var(--color-rule-strong)] bg-[var(--color-paper-high)] text-[var(--color-vermilion)]",
  solid:
    "rounded-[6px] border border-[var(--color-vermilion)] bg-[var(--color-vermilion)] text-white",
  neutral:
    "rounded-[6px] border border-[var(--color-rule-strong)] bg-[var(--color-paper-high)] text-[var(--color-ink)]",
  ordinal:
    "rounded-none border border-transparent bg-transparent text-[var(--color-vermilion)]",
} as const

export function MarketingBadge({
  children,
  variant = "outline",
  size = "sm",
  className,
  ...props
}: MarketingBadgeProps) {
  return (
    <span
      data-marketing-badge={variant}
      className={cn(
        "inline-flex w-fit shrink-0 items-center gap-1.5 whitespace-nowrap font-mono font-bold uppercase leading-none",
        variant === "ordinal" ? ORDINAL_SIZE[size] : SIZE[size],
        VARIANT[variant],
        className,
      )}
      {...props}
    >
      {children}
    </span>
  )
}
